import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  total: number;
  limit: number;
  offset: number;
  onChange: (newOffset: number) => void;
  className?: string;
}

export function Pagination({ total, limit, offset, onChange, className }: Props) {
  const safeLimit = Math.max(1, limit);
  const hasAny = total > 0;
  const start = hasAny ? offset + 1 : 0;
  const end = hasAny ? Math.min(offset + safeLimit, total) : 0;

  const canPrev = offset > 0;
  const canNext = offset + safeLimit < total;

  const goPrev = () => {
    if (!canPrev) return;
    onChange(Math.max(0, offset - safeLimit));
  };
  const goNext = () => {
    if (!canNext) return;
    onChange(offset + safeLimit);
  };

  const btnClass =
    'inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-bb-lilac bg-white text-bb-navy hover:bg-bb-navy/5 disabled:opacity-50 disabled:hover:bg-white disabled:cursor-not-allowed';

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-1 py-2 text-sm text-slate-600',
        className,
      )}
    >
      <div>
        {hasAny ? (
          <>
            Showing <span className="font-medium text-slate-800">{start}</span>-
            <span className="font-medium text-slate-800">{end}</span> of{' '}
            <span className="font-medium text-slate-800">{total}</span>
          </>
        ) : (
          <span className="text-slate-500">No results</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev}
          className={btnClass}
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={!canNext}
          className={btnClass}
          aria-label="Next page"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
